import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { buildTransformedUserRateList, modifyUserSelectedProjects } from 'factories/user.editor';
import { getTagsForProjectFilter } from 'redux/modules/tags.selectors';
import {
  PROJECT_EDITOR_DROPDOWN_KEY,
  PROJECT_EDITOR_RATES_LIST_KEY,
} from 'redux/modules/user.editor.constants';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import {
  getAccountCurrency,
  getAccountFeatures,
  getCurrentAccount,
  getHasReachedSeatsLimit,
  getHasReachedUserLimit,
  getIsActiveTrial,
  getNumberOfActiveProjectsInWorkspace,
  getTeamsFeatureEnabled,
  getUserAccountProperties,
} from 'redux/selectors/accounts';
import { getActiveClients } from 'redux/selectors/clients';
import { getProjectsFetchingStatus } from 'redux/selectors/projects';
import { getTeamEntities, getTeams } from 'redux/selectors/teams';
import { getUser } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Currency } from 'types/account';
import type { UserRole } from 'types/role';
import type { Team } from 'types/team';
import type { EnrichedUsersDraftProject, UsersDraft } from 'types/users';
import { groupBy, objectNumberKeys } from 'utils/typesafe';
import { getCurrenciesById } from './currencies';
import { getUserRoles } from './roles';
import { getPublicReports, getReports } from './superreports';
import { enrichLightProject } from './utils/projects';

const readUserEditorState = (state: IState) => state.userEditor;
const readProjectIdsByTagId = (state: IState) => state.userEditor.projectsIdsByTagId;

export const getXhrStatus = createSelector(
  [readUserEditorState],
  editorState => editorState.xhrStatus
);
const getXhrUiStatus = createSelector([getXhrStatus], xhrStatusUIState);

const readUserEditorPagination = (state: IState) => state.userEditor.pagination;

export const getUserEditorPagination = readUserEditorPagination;

export const getDraft = createSelector([readUserEditorState], editor => editor.draft);
export const getOriginalDraft = createSelector(
  [readUserEditorState],
  editor => editor.draftOriginal
);

export const getProjectsForDropdown = createSelector(
  [readUserEditorState],
  state => state.projectsForDropdown
);
const getEnrichedProjectsForDropdown = createSelector(
  [getProjectsForDropdown],
  projectsForDropdown => projectsForDropdown.map(project => enrichLightProject(project))
);

export const getProjectRateFilters = createSelector(
  [readUserEditorState],
  state => state.projectRateFilters
);

const getProjectRateList = createSelector(
  [readUserEditorState],
  state => state.paginatedProjectRateList
);

// @deprecated, see below
export const hasUserDraft = createSelector([getDraft], draft => draft != null);

export const getUserDraftStatus = createSelector([hasUserDraft], exists => {
  return {
    exists, // Flesh out as needed, see getHourDraftStatus for inspiration? Standardize a type?
  };
});

export const getLoading = createSelector([getXhrUiStatus], status => status.isLoading);
const getSaving = createSelector([getXhrUiStatus], status => status.isSaving);

export const getIsEditingUser = createSelector([getDraft], draft => {
  return !!draft?.id;
});

export const getDraftProjects = createSelector([getDraft], draft => draft?.projects);

export const getSelectedProjectIds = createSelector(
  [getProjectsForDropdown, getDraftProjects],
  (projects, draftProjects) => {
    return draftProjects ? modifyUserSelectedProjects(draftProjects, projects) : EMPTY_ARRAY;
  }
);

export const getNumberOfAddedProjects = createSelector([getDraft], draft => {
  if (!draft) return 0;

  const transform = draft.projects;
  if (transform.spec === 'replace') {
    return transform.projects.length;
  }
  return (draft.active_projects_count ?? 0) + transform.add.length - transform.remove.length;
});

export const getNumberOfAddedProjectsByClient = createSelector(
  [getDraft, getProjectsForDropdown],
  (draft, projects) => {
    if (!draft) return EMPTY_OBJECT;

    const projectsByClient = groupBy(projects, project => project.client.id);
    const transform = draft.projects;
    const addedProjectsByClient = { ...draft.addedProjectsByClient };
    // Map over it, find projects with that client id, and add/remove depending on transform
    objectNumberKeys(addedProjectsByClient).forEach(clientId => {
      const projectIdsInClient = new Set(
        (projectsByClient[clientId] ?? []).map(project => project.id)
      );
      if (transform.spec === 'replace') {
        addedProjectsByClient[clientId] = transform.projects.filter(project =>
          projectIdsInClient.has(project.project_id)
        ).length;
        return;
      }
      const addedIdsForClient = transform.add.filter(project =>
        projectIdsInClient.has(project.project_id)
      );
      const removedIdsForClient = transform.remove.filter(id => projectIdsInClient.has(id));
      addedProjectsByClient[clientId] += addedIdsForClient.length - removedIdsForClient.length;
    });
    return addedProjectsByClient;
  }
);

export const getHasChangedInternalHourRate = createSelector(
  [getDraft, getOriginalDraft],
  (draft, originalDraft) => {
    return draft?.internal_hour_rate !== originalDraft?.internal_hour_rate;
  }
);

// A project used to be required here, on the reasoning that a user without one
// can't do anything in the app. It also blocked every edit of an existing user
// who happened to have no projects, with no way out: the save button stayed
// disabled whatever you changed (TT-5570). Workspaces that haven't imported
// their projects yet couldn't invite anyone either. Projects are assignable
// from the edit flow at any time, so name and email are the real requirements.
export const getIsUserValid = createSelector([getDraft], draft => {
  if (!draft) return false;

  return [draft.name && draft.name !== '', draft.email && draft.email !== ''].every(
    validation => validation === true
  );
});

const getUserNeedsToDecideDefaultRateApplication = createSelector([getDraft], draft => {
  if (!draft?.id) {
    // This choice does not apply to invited users
    return false;
  }
  const needsChoice = draft.default_hour_rate !== draft.initial_default_hour_rate;
  const choiceMade = draft.apply_default_rate != null;
  return needsChoice && !choiceMade;
});

export const getUserAccountPropertyDraftValues = createSelector(
  [getDraft, getUserAccountProperties],
  (draft, originalProps) => {
    if (!draft) return {};
    const originalUserAccountProperties = originalProps[draft.id!] ?? [];
    const { userAccountProperties } = draft;
    const idValueMap: Record<number, string> = {};
    originalUserAccountProperties.forEach(originalProperty => {
      idValueMap[originalProperty.account_user_property_id] = originalProperty.value;
    });
    return { ...idValueMap, ...userAccountProperties };
  }
);

const getUserNeedsToDecideInternalCostRateApplication = createSelector(
  [getDraft, getHasChangedInternalHourRate],
  (draft, hasChangedInternalHourRate) => {
    if (!draft?.id) {
      // This choice does not apply to invited users
      return false;
    }

    return (
      hasChangedInternalHourRate &&
      draft.update_existing_hours_internal_rate === undefined &&
      draft.update_unbilled_hours_internal_rate === undefined
    );
  }
);

export const getIsSaveDisabled = createSelector(
  [
    getSaving,
    getLoading,
    getIsEditingUser,
    getHasReachedUserLimit,
    getIsUserValid,
    getUserNeedsToDecideDefaultRateApplication,
    getUserNeedsToDecideInternalCostRateApplication,
  ],
  (
    isSaving,
    isLoading,
    isEditingUser,
    isUserLimitReached,
    isUserValid,
    userNeedsToDecideDefaultRateApplication,
    userNeedsToDecideInternalCostRateApplication
  ) => {
    const willExceedUserLimit = !isEditingUser && isUserLimitReached;
    return (
      isSaving ||
      isLoading ||
      willExceedUserLimit ||
      !isUserValid ||
      userNeedsToDecideDefaultRateApplication ||
      userNeedsToDecideInternalCostRateApplication
    );
  }
);

const getDraftRateList = createSelector(
  [getProjectRateList, getDraftProjects, getProjectsForDropdown],
  (rates, projects, projectsForDropdown): Array<EnrichedUsersDraftProject> => {
    if (!projects) {
      return EMPTY_ARRAY;
    }
    return buildTransformedUserRateList(rates, projects, projectsForDropdown);
  }
);

const getFilteredDraftRateList = createSelector(
  [getDraftRateList, getProjectRateFilters],
  (rateList, filters) => {
    const searchWords = filters.queryString.toLowerCase().split(' ');
    return rateList.filter(p => {
      if (filters.rateType != null && p.rateType !== filters.rateType) {
        return false;
      }
      if (searchWords.length < 1) {
        return true;
      }
      const projectName = p.project.name.toLowerCase() ?? '';
      const clientName = p.project.client.name.toLowerCase() ?? '';
      return searchWords.every(
        search => clientName.includes(search) || projectName.includes(search)
      );
    });
  }
);

const getDraftRateListWithOverrides = createSelector(
  [getFilteredDraftRateList, getDraft],
  (rates, draft) => {
    if (!draft) {
      return rates;
    }

    const { apply_default_rate, initial_default_hour_rate, default_hour_rate } = draft;
    if (apply_default_rate == null) {
      return rates;
    }
    return rates.map(rate => {
      const { rateType, draftRate } = rate;
      const matchesInitialDefaultRate = draftRate === initial_default_hour_rate;
      const defaultRateWillBeApplied =
        apply_default_rate === 'all' ||
        apply_default_rate === 'all_without_history' ||
        (apply_default_rate === 'matching' && matchesInitialDefaultRate);
      if (rateType === 'user' && defaultRateWillBeApplied) {
        return {
          ...rate,
          rateOverride: default_hour_rate,
        };
      }

      return rate;
    });
  }
);

const getRatesHasInitialized = createSelector(
  [getXhrStatus, getDraft],
  (xhrStatus, draft) =>
    xhrStatusUIState(xhrStatus, PROJECT_EDITOR_RATES_LIST_KEY).hasInitialized || draft?.id == null
);

const getUserEditorRatesListProps = createSelector(
  [getDraftRateListWithOverrides, getProjectRateFilters, getRatesHasInitialized],
  (projects, filters, hasInitialized) => ({
    projects,
    filters,
    hasInitialized,
  })
);

const getUserEditorDropdownProps = createSelector(
  [
    getActiveClients,
    getEnrichedProjectsForDropdown,
    getSelectedProjectIds,
    getNumberOfAddedProjects,
    getNumberOfAddedProjectsByClient,
    getNumberOfActiveProjectsInWorkspace,
    getXhrStatus,
    getProjectsFetchingStatus,
    getTagsForProjectFilter,
    readProjectIdsByTagId,
  ],
  (
    clients,
    projects,
    selectedIds,
    numSelectedProjects,
    numSelectedProjectsByClient,
    numTotalProjects,
    xhrStatus,
    projectFetchingStatus,
    tags,
    projectIdsByTagId
  ) => {
    return {
      clients,
      projects,
      selectedIds,
      numSelectedProjects,
      numSelectedProjectsByClient,
      numTotalProjects,
      numActiveProjects: numTotalProjects,
      isLoading: xhrStatusUIState(xhrStatus, PROJECT_EDITOR_DROPDOWN_KEY).isLoading,
      projectFetchingStatus,
      tags,
      projectIdsByTagId,
    };
  }
);

export const getDraftTeamIds = createSelector(
  [getDraft],
  (draft): Array<number> =>
    draft?.teams && draft.teams.length ? draft.teams.map(t => t.id || 0) : EMPTY_ARRAY
);

interface UserEditorUi {
  showUserLimitWarning: boolean;
  showTeamsSection: boolean;
  isTeamsFeatureEnabled: boolean;
  isHourlyRateFeatureEnabled: boolean;
  /** Weekly capacity is a workspace-level feature (applies to all) */
  isWeeklyUserCapacityFeatureEnabled: boolean;
  /** Individual capacity us a per-user setting */
  isIndividualUserCapacityFeatureEnabled: boolean;
  isSaveDisabled: boolean;
  isEditingUser: boolean;
  isBasicInfoFormDisabled: boolean;
  isFormDisabled: boolean;
  isUserAdmin: boolean;
  isWorkspaceEnterprise: boolean;
  labels: {
    header: string;
    submitButton: string;
  };
  isSaving: boolean;
  willApplyDefaultRates: boolean;
  hasLivePublicReports: boolean;
  hasChangedInternalHourRate: boolean;
}

export interface UserEditorState {
  draft: UsersDraft;
  dropdown: ReturnType<typeof getUserEditorDropdownProps>;
  ratesList: ReturnType<typeof getUserEditorRatesListProps>;
  teams: Array<Team>;
  selectedTeamIds: Array<number>;
  currency: Currency;
  ui: UserEditorUi;
  userRoles: Array<UserRole>;
  currencyLookup: Record<string, Currency>;
}

export const getShowUserLimitWarning = createSelector(
  [getHasReachedSeatsLimit, getIsEditingUser, getIsActiveTrial],
  (reachedSeatsLimit, isEditingUser, isActiveTrial) =>
    // When the workspace has reached the seats limit
    reachedSeatsLimit &&
    // And a new user is being created
    !isEditingUser &&
    // And there is no active trial
    !isActiveTrial
);

// Reselect's typings don't support more than 12 selectors, so we need to split it or TypeScript
// will break
const getUserEditorUi1 = createSelector(
  [
    getHasReachedSeatsLimit,
    getTeamsFeatureEnabled,
    getIsSaveDisabled,
    getIsEditingUser,
    getSaving,
    getIsActiveTrial,
    getShowUserLimitWarning,
  ],
  (
    reachedSeatsLimit,
    isTeamsFeatureEnabled,
    isSaveDisabled,
    isEditingUser,
    isSaving,
    isActiveTrial,
    showUserLimitWarning
  ) => {
    return {
      reachedSeatsLimit,
      isTeamsFeatureEnabled,
      isSaveDisabled,
      isEditingUser,
      isSaving,
      isActiveTrial,
      showUserLimitWarning,
    };
  }
);

const getUserEditorUi = createSelector(
  [
    getDraft,
    getCurrentAccount,
    getUser,
    getAccountFeatures,
    getProjectsFetchingStatus,
    getReports,
    getPublicReports,
    getHasChangedInternalHourRate,
    getUserEditorUi1,
  ],
  (
    draft,
    account,
    user,
    features,
    projectFetchingStatus,
    reports,
    publicReports,
    hasChangedInternalHourRate,
    ui1
  ) => {
    const { isEditingUser, isSaving } = ui1;

    const hasLivePublicReports = reports.some(report => {
      if (report.createdBy.id !== draft?.id) return false;

      return publicReports.some(
        publicReport => publicReport.live && report.publicReportIds.includes(publicReport.id)
      );
    });

    return {
      ...ui1,
      canEditTeams: draft?.id == null,
      isUserAdmin: user.admin,
      isWorkspaceEnterprise: !!account.plan_custom,
      labels: {
        header: isEditingUser ? `${draft?.name}'s Permissions` : 'Invite new person',
        submitButton: isEditingUser ? 'Update person' : 'Invite person',
      },
      isHourlyRateFeatureEnabled: !!features.budgets_hourly_rates,
      isIndividualUserCapacityFeatureEnabled: !!features.individual_capacity,
      isWeeklyUserCapacityFeatureEnabled: !!features.weekly_user_capacity,
      isBasicInfoFormDisabled: isEditingUser || isSaving,
      isFormDisabled: isSaving,
      willApplyDefaultRates: projectFetchingStatus === 'fetchedAllFull',
      hasLivePublicReports,
      hasChangedInternalHourRate,
      isSaving,
    };
  }
);

export const getTeamAssociationsByProjectId = createSelector(
  [getDraft, getTeamEntities],
  (userDraft, teamsById) => {
    const teamsByProjectId: Record<number, Array<Team>> = {};
    (userDraft?.teams ?? []).forEach(({ id }) => {
      if (id) {
        const team = teamsById[id];
        team.project_ids?.forEach((projectId: number) => {
          teamsByProjectId[projectId] = teamsByProjectId[projectId] ?? [];
          teamsByProjectId[projectId].push(team);
        });
      }
    });
    return teamsByProjectId;
  }
);

export const getUserEditorData = createSelector(
  [
    getDraft,
    getTeams,
    getDraftTeamIds,
    getAccountCurrency,
    getUserEditorUi,
    getUserRoles,
    getUserEditorDropdownProps,
    getUserEditorRatesListProps,
    getCurrenciesById,
    getTeamAssociationsByProjectId,
  ],
  (
    draft,
    teams,
    selectedTeamIds,
    currency,
    ui,
    userRoles,
    dropdown,
    ratesList,
    currencyLookup,
    teamAssociationsByProjectId
  ) => {
    return {
      draft,
      teams,
      selectedTeamIds,
      currency,
      ui,
      userRoles,
      dropdown,
      ratesList,
      currencyLookup,
      teamAssociationsByProjectId,
    };
  }
);
